import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectIsAIAutoMode } from '../../redux/features/ai/ai.selector';
import { selectCurrentTaskAIStatus } from '../../redux/features/taskRouter/taskRouter.selector';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import ConfirmDialog from '../common/ConfirmDialog';
import { VolumeSlider } from '../Audio/VolumeSlider';
import { MessageContainer } from '../Chat/MessageContainer';
import useTranscriptMessages from './useTranscriptMessages.hooks';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import useStickyScroll from '../../hooks/useStickyScroll.hooks';

type TranscriptSliderAreaTypeProps = {
  isExpandedMode: boolean;
};

const useStyles = makeStyles<Theme, TranscriptSliderAreaTypeProps>(
  ({ spacing }) => ({
    transcriptSliderAreaClass: {
      position: 'absolute',
      zIndex: '5',
      height: 'calc(100% - 243px)',
      borderRight: `${spacing(0.125)} solid ${Colors.alto}`,
    },
    closeClass: {
      position: 'absolute',
      top: '0',
      right: '0',
    },
    transcriptMessagesClass: {
      overflowY: 'auto',
      height: `calc(100% - 40px)`,
      borderTop: `${spacing(0.125)} solid ${Colors.alto}`,
    },
    dividerContainerClass: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: '10px',
    },
    dividerClass: {
      fontSize: '14px',
      lineHeight: '20px',
      color: Colors.guardsmanRed,
    },
    underlineClass: {
      flex: '1',
      content: '',
      padding: '0.75px',
      backgroundColor: Colors.guardsmanRed,
      margin: '5px',
      display: 'inline-block',
    },
  })
);

const TranscriptSliderArea = () => {
  const [isExpandedMode, setIsExpandedMode] = useState(false);
  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const eventAPI = useAppSelector(selectEventAPI);
  const eventAPIAuth = useAppSelector(selectEventAPIAuth);
  const restaurantCode = useAppSelector(selectActiveRestaurantCode);
  const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
  const dispatch = useAppDispatch();
  const effectiveSessionId = useAppSelector(
    (state) =>
      state.taskRouter.taskAssignedSessionId ||
      state.sessionBoundary.sessionId ||
      state.sessionBoundary.endedSessionId ||
      state.messages.currentSessionId
  );

  const {
    transcriptSliderAreaClass,
    closeClass,
    dividerContainerClass,
    transcriptMessagesClass,
    dividerClass,
    underlineClass,
  } = useStyles({
    isExpandedMode,
  });

  useEffect(() => {
    if (currentTaskAIStatus) {
      setIsExpandedMode(true);
    }
  }, []);

  useEffect(() => {
    if (!isAIAutoMode && isExpandedMode) {
      setIsExpandedMode(false);
    }
  }, [isAIAutoMode]);

  const { messagesList, sessionBufferMessagesList } = useTranscriptMessages();

  // HITL-1481: this div is the real scroll container, so follow-mode is owned
  // here (MessageContainer used to try, against an element that never scrolls).
  // Legacy layout gets the scroll-position fix only — the HITL 3.0 "New
  // messages" pill stays behind the hitl_3_0 UI. The takeover toggle joins the
  // reset key: it swings the pane from 360px to full width, so the content
  // reflows and the old scroll offset no longer points at what the agent was
  // reading.
  const { scrollRef, handleScroll } = useStickyScroll(
    [messagesList, sessionBufferMessagesList],
    `${effectiveSessionId}:${isExpandedMode}`
  );

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleTranscriptAreaClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      // If any dialog is open anywhere in the document, ignore this click.
      if (
        typeof document !== 'undefined' &&
        document.querySelector('[role="dialog"]')
      ) {
        return;
      }
      if (
        target &&
        (target.closest('[data-word]') || target.closest('[role="dialog"]'))
      ) {
        return;
      }

      setConfirmDialogOpen(true);
    },
    [eventAPI, eventAPIAuth, restaurantCode, effectiveSessionId, userDetails]
  );

  const handleConfirmBlankClick = useCallback(() => {
    sendAsrIncorrectEvent({
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      wordClicked: 'ASR MISSING',
      dispatch,
    });

    setConfirmDialogOpen(false);
  }, [
    eventAPI,
    eventAPIAuth,
    restaurantCode,
    effectiveSessionId,
    userDetails,
    dispatch,
  ]);

  return (
    <div
      data-testid="transcript-slider-area"
      className={transcriptSliderAreaClass}
      style={{
        width: isExpandedMode ? '100%' : '360px',
        background: isExpandedMode ? Colors.white : Colors.porcelain,
      }}
    >
      <VolumeSlider />
      <div
        className={transcriptMessagesClass}
        data-transcript="true"
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          padding: isExpandedMode ? '32px' : '6px',
          paddingLeft: isExpandedMode ? '26px' : '0',
        }}
        onClick={handleTranscriptAreaClick}
      >
        {sessionBufferMessagesList.length > 0 && (
          <MessageContainer messageList={sessionBufferMessagesList} />
        )}
        {currentTaskAIStatus && sessionBufferMessagesList.length > 0 && (
          <>
            <div className={dividerContainerClass}>
              <div className={underlineClass}></div>
              <div className={dividerClass}>With Supervised Agent</div>
              <div className={underlineClass}></div>
            </div>
          </>
        )}
        <MessageContainer messageList={messagesList} />
      </div>
      {isExpandedMode && (
        <div className={closeClass}>
          <IconButton
            aria-label="delete"
            onClick={() => setIsExpandedMode(false)}
          >
            <CloseIcon />
          </IconButton>
        </div>
      )}
      <ConfirmDialog
        // Portal-rendered, so it needs the transcript marker itself — confirming
        // an ASR report is still not taking over the order.
        data-transcript="true"
        open={confirmDialogOpen}
        content={'Send ASR incorrect event for word: "ASR MISSING"?'}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmBlankClick}
        closeBtnText="Cancel"
        confirmBtnText="OK"
      />
    </div>
  );
};

export default TranscriptSliderArea;
